<template>
  <div class="homepage-sections-container">
    <div class="section-header">
      <h1>Homepage Sections</h1>
    </div>

    <div v-if="isLoading" class="loading-state">
      <div v-for="n in 3" :key="n" class="loading-section">
        <div class="loading-shimmer"></div>
      </div>
    </div>

    <div v-else-if="error" class="error-message">
      <p>{{ error }}</p>
      <button @click="fetchSections" class="retry-button">Retry</button>
    </div>

    <div v-else-if="sections.length === 0" class="empty-state">
      <p>No homepage sections found.</p>
    </div>

    <div v-else class="sections-list">
      <div v-for="section in orderedSections" :key="section.id" class="section-card">
        <div class="section-info" @click="viewSectionDetail(section.id)">
          <div class="section-type-badge" :class="section.type.toLowerCase().replace(/\s+/g, '-')">
            {{ section.type }}
          </div>
          <h2 class="section-title">{{ section.title }}</h2>
          <p v-if="section.subtitle" class="section-subtitle">{{ section.subtitle }}</p>
          
          <div class="section-details">
            <div class="detail-item">
              <span class="detail-label">Method:</span>
              <span class="detail-value">{{ section.method_name }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Cache Key:</span>
              <span class="detail-value">{{ section.cache_key }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Order:</span>
              <span class="detail-value">{{ section.order }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Status:</span>
              <span class="detail-value" :class="section.is_active ? 'active' : 'inactive'">
                {{ section.is_active ? 'Active' : 'Inactive' }}
              </span>
            </div>
          </div>

          <div class="section-stats">
            <div class="stat-item">
              <span class="stat-value">{{ section.courses_count }}</span>
              <span class="stat-label">Total Courses</span>
            </div>
            <div class="stat-item">
              <span class="stat-value">{{ section.active_courses_count }}</span>
              <span class="stat-label">Active Courses</span>
            </div>
          </div>
        </div>
        
        <div class="section-actions">
          <button class="view-button" @click="viewSectionDetail(section.id)">
            <i class="fas fa-eye"></i> View
          </button>
          <button class="edit-button" @click="editSection(section.id)">
            <i class="fas fa-edit"></i> Edit
          </button>
          <button class="update-button" @click="updateSection(section.id)">
            <i class="fas fa-sync-alt"></i> Update
          </button>
          <button class="toggle-button" @click.stop="toggleSectionStatus(section)">
            <i :class="section.is_active ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            {{ section.is_active ? 'Deactivate' : 'Activate' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';

export default {
  name: 'HomepageSections',
  setup() {
    const router = useRouter();
    const sections = ref([]);
    const isLoading = ref(true);
    const error = ref(null);

    const orderedSections = computed(() => {
      return [...sections.value].sort((a, b) => a.order - b.order);
    });

    const fetchSections = async () => {
      isLoading.value = true;
      error.value = null;
      
      try {
        const token = localStorage.getItem('token') ;
        
        const response = await axios.get('https://api.kewaar.com/api/admin/homepage-sections', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        
        if (response.data && response.data.success) {
          sections.value = response.data.data;
        } else {
          error.value = 'Failed to load homepage sections: ' + (response.data.message || 'Unknown error');
        }
      } catch (err) {
        console.error('Error fetching homepage sections:', err);
        error.value = 'Failed to load homepage sections: ' + (err.message || 'Network error');
      } finally {
        isLoading.value = false;
      }
    };

    const viewSectionDetail = (sectionId) => {
      router.push(`/homepage/sections/${sectionId}`);
    };

    const editSection = (sectionId) => {
      router.push(`/homepage/sections/${sectionId}/edit`);
    };

    const updateSection = (sectionId) => {
      router.push(`/homepage/sections/${sectionId}/update`);
    };

    const toggleSectionStatus = async (section) => {
      try {
        const token = localStorage.getItem('token') ;
        
        const response = await axios.patch(
          `https://api.kewaar.com/api/admin/homepage-sections/${section.id}/toggle-status`,
          {},
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          }
        );
        
        if (response.data && response.data.success) {
          // Update the section status in the local state
          const updatedSection = response.data.data;
          const index = sections.value.findIndex(s => s.id === updatedSection.id);
          if (index !== -1) {
            sections.value[index] = updatedSection;
          }
        } else {
          console.error('Failed to toggle section status:', response.data.message);
        }
      } catch (err) {
        console.error('Error toggling section status:', err);
      }
    };

    onMounted(() => {
      fetchSections();
    });

    return {
      sections,
      orderedSections,
      isLoading,
      error,
      fetchSections,
      viewSectionDetail,
      editSection,
      updateSection,
      toggleSectionStatus
    };
  }
};
</script>

<style scoped>
.homepage-sections-container {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.section-header {
  margin-bottom: 2rem;
}

.section-header h1 {
  font-size: 1.8rem;
  color: #2D3748;
  margin: 0;
}

.loading-state {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.loading-section {
  height: 180px;
  background: #f1f5f9;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
}

.loading-shimmer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.6) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: shimmer 1.5s infinite;
  transform: translateX(-100%);
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}

.error-message {
  background: #FEE2E2;
  color: #B91C1C;
  padding: 1.5rem;
  border-radius: 8px;
  text-align: center;
  margin: 2rem 0;
}

.retry-button {
  background: #DC2626;
  color: white;
  border: none;
  padding: 0.5rem 1.5rem;
  border-radius: 6px;
  margin-top: 1rem;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.retry-button:hover {
  background: #B91C1C;
}

.empty-state {
  background: #F3F4F6;
  padding: 3rem;
  border-radius: 8px;
  text-align: center;
  color: #6B7280;
  font-size: 1.1rem;
}

.sections-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
}

.section-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
  overflow: hidden;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
}

.section-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.05), 0 4px 6px rgba(0, 0, 0, 0.05);
}

.section-info {
  padding: 1.5rem;
  cursor: pointer;
}

.section-type-badge {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 1rem;
  background: #E5E7EB;
  color: #4B5563;
}

.section-type-badge.banner {
  background: #DBEAFE;
  color: #1E40AF;
}

.section-type-badge.fullcardcarousel {
  background: #DEF7EC;
  color: #046C4E;
}

.section-type-badge.squarecardcarouselv1,
.section-type-badge.squarecardcarouselv2 {
  background: #FEF3C7;
  color: #92400E;
}

.section-type-badge.tallcardcarousel {
  background: #FCE7F3;
  color: #9D174D;
}

.section-type-badge.gridcarousel {
  background: #E0E7FF;
  color: #3730A3;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1F2937;
  margin: 0 0 0.5rem 0;
}

.section-subtitle {
  font-size: 0.875rem;
  color: #6B7280;
  margin: 0 0 1rem 0;
}

.section-details {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.detail-item {
  display: flex;
  flex-direction: column;
}

.detail-label {
  font-size: 0.75rem;
  color: #6B7280;
  margin-bottom: 0.25rem;
}

.detail-value {
  font-size: 0.875rem;
  color: #374151;
  font-weight: 500;
  word-break: break-word;
}

.detail-value.active {
  color: #059669;
}

.detail-value.inactive {
  color: #DC2626;
}

.section-stats {
  display: flex;
  gap: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid #E5E7EB;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1F2937;
}

.stat-label {
  font-size: 0.75rem;
  color: #6B7280;
  margin-top: 0.25rem;
}

.section-actions {
  display: flex;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-top: 1px solid #E5E7EB;
  background: #F9FAFB;
}

.view-button,
.edit-button,
.update-button,
.toggle-button {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.view-button {
  background: #F3F4F6;
  color: #4B5563;
}

.view-button:hover {
  background: #E5E7EB;
  color: #1F2937;
}

.edit-button {
  background: #EFF6FF;
  color: #3B82F6;
}

.edit-button:hover {
  background: #DBEAFE;
  color: #2563EB;
}

.update-button {
  background: #F0FDF4;
  color: #10B981;
}

.update-button:hover {
  background: #DCFCE7;
  color: #059669;
}

.toggle-button {
  background: #FEF2F2;
  color: #EF4444;
}

.toggle-button:hover {
  background: #FEE2E2;
  color: #DC2626;
}

@media (max-width: 768px) {
  .homepage-sections-container {
    padding: 1rem;
  }
  
  .sections-list {
    grid-template-columns: 1fr;
  }
}
</style> 